import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/use-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Definir roles y permisos
export const ROLES = {
  ADMIN: 'admin',
  SUPERVISOR: 'supervisor',
  EMPLOYEE: 'operador',
  CLIENT: 'cliente',
  CLIENT_PRENDAS: 'cliente_prendas',
  LAUNDRY: 'lavanderia',
  CENTRO_LAVADO: 'centro_lavado',
  DOCUMENTATION: 'documentacion',
  CONDUCTOR: 'conductor',
  // Rol con acceso EXCLUSIVO al módulo de Servicios Públicos.
  // Pensado para usuarios que solo deben registrar consumos de
  // servicios públicos (agua, luz, gas) sin ver nada más del sistema.
  SERVICES_PUBLIC: 'servicios_publicos',
};

// Módulos de la aplicación
export const MODULES = {
  DASHBOARD: 'dashboard',
  WORKPLACES: 'workplaces',
  CLIENTS: 'clients',
  EMPLOYEES: 'employees',
  GARMENT_TYPES: 'garment_types',
  WASHING_PROGRAMS: 'washing_programs',
  MACHINES: 'machines',
  REPORTS: 'reports',
  SETTINGS: 'settings',
  USERS: 'users',
  LAUNDRIES: 'laundries',
  RECEPTION: 'reception',
  PRODUCTS: 'products',
  SERVICE: 'services',
  PROVIDERS: 'providers',
  OPERARIO: 'operario',
  DOCUMENT_TYPES: 'document_types',
  DOCUMENTATION: 'documentation',
  LAUNDRIES_CENTERS: 'laundries_centers',
  // Facturación
  BILLING: 'billing',              // Ver módulo Facturación
  BILLING_GENERATE: 'billing_generate', // Generar liquidaciones
  BILLING_DELETE: 'billing_delete',     // Eliminar facturas
  CONTROL_EMPLOYEES: 'control_employees', // Control de empleados
  EMPLOYEE_PAYROLL: 'employee_payroll',   // Liquidación personal
  SOLICITUDES: 'solicitudes',             // Tareas y solicitudes
  CLIENT_PORTAL: 'client_portal',           // Portal de clientes
  CLIENT_PRENDAS: 'client_prendas',         // Portal cliente prendas
  CONDUCTOR: 'conductor',                   // Módulo de conductor
  RFID_REGISTRO: 'rfid_registro',             // Registro de prendas RFID
  FINANCIERA: 'financiera',                   // Módulo Financiera
  VEHICLES: 'vehicles',                       // Módulo de vehículos
  UTILITY_SERVICES: 'utility_services',       // Servicios públicos (consumos)
  AUTHORIZED_HOURS: 'horas_autorizadas',      // Horas autorizadas por semana para la planificación de turnos
  PERSONAL: 'personal',                       // Módulo de Personal (RR. HH.): incapacidades, permisos, comunicaciones y pagos de nómina
};

// Permisos por rol (por defecto). Admin solo ve Facturación; generar/eliminar controlados por módulos personalizados
const ROLE_PERMISSIONS = {
  [ROLES.ADMIN]: [
    MODULES.DASHBOARD,
    MODULES.WORKPLACES,
    MODULES.CLIENTS,
    MODULES.EMPLOYEES,
    MODULES.GARMENT_TYPES,
    MODULES.WASHING_PROGRAMS,
    MODULES.MACHINES,
    MODULES.REPORTS,
    MODULES.SETTINGS,
    MODULES.USERS,
    MODULES.LAUNDRIES,
    MODULES.RECEPTION,
    MODULES.PRODUCTS,
    MODULES.SERVICE,
    MODULES.PROVIDERS,
    MODULES.OPERARIO,
    MODULES.DOCUMENT_TYPES,
    MODULES.DOCUMENTATION,
    MODULES.LAUNDRIES_CENTERS,
    MODULES.BILLING,
    MODULES.BILLING_GENERATE,
    MODULES.BILLING_DELETE,
    MODULES.CONTROL_EMPLOYEES,
    MODULES.EMPLOYEE_PAYROLL,
    MODULES.SOLICITUDES,
    MODULES.RFID_REGISTRO,
    MODULES.FINANCIERA,
    MODULES.VEHICLES,
    MODULES.UTILITY_SERVICES,
    MODULES.AUTHORIZED_HOURS,
    MODULES.PERSONAL,
  ],
  [ROLES.SUPERVISOR]: [
    MODULES.DASHBOARD,
    MODULES.WORKPLACES,
    MODULES.REPORTS,
    MODULES.RECEPTION,
    MODULES.CONTROL_EMPLOYEES,
    MODULES.UTILITY_SERVICES,
    // El supervisor registra incapacidades y permisos desde el módulo de
    // Personal, así que lo necesita igual que el control de empleados.
    MODULES.PERSONAL,
  ],
  [ROLES.EMPLOYEE]: [
    MODULES.DASHBOARD,
    MODULES.OPERARIO,
  ],
  [ROLES.CLIENT]: [
    MODULES.DASHBOARD,
    MODULES.CLIENT_PORTAL,
  ],
  [ROLES.LAUNDRY]: [
    MODULES.DASHBOARD,
  ],
  [ROLES.CENTRO_LAVADO]: [
    MODULES.DASHBOARD,
    MODULES.WORKPLACES,
    MODULES.REPORTS,
    // El rol de Centro de Lavado debe poder gestionar el módulo
    // de Servicios Públicos (consumos de agua/luz/gas) del centro.
    MODULES.UTILITY_SERVICES,
  ],
  [ROLES.DOCUMENTATION]: [
    MODULES.DASHBOARD,
    MODULES.DOCUMENTATION,
    MODULES.EMPLOYEES,
    MODULES.DOCUMENT_TYPES,
  ],
  [ROLES.CLIENT_PRENDAS]: [
    MODULES.CLIENT_PRENDAS,
  ],
  [ROLES.CONDUCTOR]: [
    MODULES.CONDUCTOR,
  ],
  // Rol de servicios públicos: acceso EXCLUSIVO al módulo de
  // Servicios Públicos de SU centro. NO se le da WORKPLACES a
  // propósito: ese módulo abre todo el centro (recepción, ciclos,
  // horas, inventarios, etc.). La ruta del LaundryDashboard acepta
  // utility_services como alternativa a workplaces (ver App.jsx),
  // así puede entrar al dashboard de su centro pero solo ve/abre la
  // card de Servicios Públicos. El link "Centros" del navbar se
  // oculta por separado para que no navegue a otros centros.
  [ROLES.SERVICES_PUBLIC]: [
    MODULES.UTILITY_SERVICES,
  ],
};

// Catalogo de modulos para la UI de permisos por usuario. Agrupado y con
// etiqueta legible; es lo que pinta AsignarModulosUsuarioModal.
export const MODULE_GROUPS = [
  {
    id: 'reportes',
    label: 'Reportes',
    modules: [
      { key: MODULES.REPORTS, label: 'Reportes y Dashboard' },
      { key: MODULES.FINANCIERA, label: 'Financiera' },
    ],
  },
  {
    id: 'operacion',
    label: 'Operacion',
    modules: [
      { key: MODULES.WORKPLACES, label: 'Centros de lavado' },
      { key: MODULES.RECEPTION, label: 'Recepcion' },
      { key: MODULES.UTILITY_SERVICES, label: 'Servicios publicos' },
      { key: MODULES.OPERARIO, label: 'Operario' },
      { key: MODULES.RFID_REGISTRO, label: 'Registro RFID' },
      { key: MODULES.SOLICITUDES, label: 'Tareas y solicitudes' },
    ],
  },
  {
    id: 'administrativo',
    label: 'Administrativo',
    modules: [
      { key: MODULES.PERSONAL, label: 'Personal' },
      { key: MODULES.CONTROL_EMPLOYEES, label: 'Control de personal' },
      { key: MODULES.EMPLOYEE_PAYROLL, label: 'Nomina' },
      { key: MODULES.BILLING, label: 'Facturacion' },
      { key: MODULES.BILLING_GENERATE, label: 'Generar liquidaciones' },
      { key: MODULES.BILLING_DELETE, label: 'Eliminar facturas' },
      { key: MODULES.AUTHORIZED_HOURS, label: 'Horas autorizadas (planificación)' },
    ],
  },
  {
    id: 'parametrizacion',
    label: 'Parametrizacion',
    modules: [
      { key: MODULES.SETTINGS, label: 'Menu Parametrizacion' },
      { key: MODULES.LAUNDRIES_CENTERS, label: 'Centros / parametrizar' },
      { key: MODULES.USERS, label: 'Usuarios' },
      { key: MODULES.EMPLOYEES, label: 'Empleados' },
      { key: MODULES.CLIENTS, label: 'Clientes' },
      { key: MODULES.PRODUCTS, label: 'Productos' },
      { key: MODULES.PROVIDERS, label: 'Proveedores' },
      { key: MODULES.MACHINES, label: 'Lavadoras y secadoras' },
      { key: MODULES.GARMENT_TYPES, label: 'Tipos de prenda' },
      { key: MODULES.WASHING_PROGRAMS, label: 'Programas de lavado' },
      { key: MODULES.SERVICE, label: 'Servicios' },
      { key: MODULES.VEHICLES, label: 'Vehiculos' },
      { key: MODULES.DOCUMENT_TYPES, label: 'Tipos de archivo' },
      { key: MODULES.DOCUMENTATION, label: 'Documentacion' },
      { key: MODULES.LAUNDRIES, label: 'Lavanderias' },
    ],
  },
  {
    id: 'portales',
    label: 'Portales',
    modules: [
      { key: MODULES.DASHBOARD, label: 'Dashboard base' },
      { key: MODULES.CLIENT_PORTAL, label: 'Portal de clientes' },
      { key: MODULES.CLIENT_PRENDAS, label: 'Portal cliente prendas' },
      { key: MODULES.CONDUCTOR, label: 'Conductor' },
    ],
  },
];

// Modulos que trae un rol por defecto. Lo usa el modal de permisos para
// precargar la seleccion, de forma que personalizar nunca parta de cero.
export const getDefaultModulesForRole = (rol) => ROLE_PERMISSIONS[rol] || [];

// Nota: Admin ya incluye USERS arriba

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Da la forma que usa la app a un usuario devuelto por el backend.
  // Es el mismo mapeo en login() y en refreshUser(), asi que vive aparte.
  const normalizeUser = (backendUser = {}) => ({
    id: backendUser._id || backendUser.id,
    email: backendUser.correo || backendUser.email,
    name: backendUser.nombre || backendUser.name,
    role: backendUser.rol || backendUser.role,
    lavanderia: backendUser.lavanderia || null,
    estado: backendUser.estado,
    raw: backendUser,
  });

  // Vuelve a leer el usuario del backend y actualiza sesion + localStorage.
  //
  // Sin esto, los permisos que ve la app son la foto tomada en el login: si un
  // administrador le quita modulos o centros a alguien con la sesion abierta,
  // ese usuario seguiria viendo el menu antiguo incluso recargando la pagina.
  // Devuelve el usuario actualizado, o null si ya no es valido.
  const refreshUser = useCallback(async (userId) => {
    const id = userId || JSON.parse(localStorage.getItem('user') || '{}')?.id;
    if (!id) return null;
    try {
      const resp = await fetch(`${API_URL}/user/filter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: 1, offset: 1, query: { _id: id } }),
      });
      if (!resp.ok) return null;
      const data = await resp.json();
      const fresh = (data?.data || [])[0];
      if (!fresh) return null;
      const normalized = normalizeUser(fresh);
      setUser(normalized);
      localStorage.setItem('user', JSON.stringify(normalized));
      return normalized;
    } catch (e) {
      // Sin red se conserva la sesion guardada: mejor seguir trabajando con
      // los permisos anteriores que echar al usuario de la aplicacion.
      return null;
    }
  }, []);

  // Cargar usuario desde localStorage al iniciar y refrescarlo contra el
  // backend, para que los cambios de permisos se apliquen al recargar.
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    let stored = null;
    if (storedUser) {
      try {
        stored = JSON.parse(storedUser);
        setUser(stored);
      } catch (error) {
        console.error('Error parsing user data', error);
        localStorage.removeItem('user');
      }
    }
    if (!storedToken) {
      localStorage.removeItem('token');
    }
    if (stored?.id) {
      refreshUser(stored.id).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [refreshUser]);

  const login = async (email, password) => {
    try {
      const resp = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ correo: email, contrasena: password }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        const message = err.message || 'Credenciales inválidas';
        toast({ title: 'Error de autenticación', description: message, variant: 'destructive' });
        return false;
      }

      const data = await resp.json();
      const token = data.token || data.accessToken;
      const backendUser = data.user || data.usuario || data.data || {};

      const normalizedUser = normalizeUser(backendUser);

      if (!normalizedUser.role) {
        toast({ title: 'Error de autenticación', description: 'Usuario sin rol asignado', variant: 'destructive' });
        return false;
      }

      if (token) localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      setUser(normalizedUser);

      toast({ title: 'Inicio de sesión exitoso', description: `Bienvenido ${normalizedUser.name}` });
      return normalizedUser;
    } catch (error) {
      console.error('Login error:', error);
      toast({ title: 'Error de autenticación', description: 'No fue posible iniciar sesión. Intenta nuevamente.', variant: 'destructive' });
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const forgotPassword = async (email) => {
    try {
      const resp = await fetch(`${API_URL}/auth/forgot-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ correo: email }) });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const message = data.message || 'No fue posible procesar la solicitud';
        toast({ title: 'Recuperación de contraseña', description: message, variant: 'destructive' });
        return { ok: false };
      }
      const tokenDev = data.token;
      toast({ title: 'Recuperación de contraseña', description: 'Si el correo existe, se enviaron instrucciones.' });
      return { ok: true, token: tokenDev };
    } catch (e) {
      toast({ title: 'Recuperación de contraseña', description: 'Error de red', variant: 'destructive' });
      return { ok: false };
    }
  };

  const resetPassword = async ({ email, token, nueva }) => {
    try {
      const resp = await fetch(`${API_URL}/auth/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ correo: email, token, nueva }) });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const message = data.message || 'Token inválido o expirado';
        toast({ title: 'Restablecer contraseña', description: message, variant: 'destructive' });
        return false;
      }
      toast({ title: 'Restablecer contraseña', description: 'Contraseña actualizada' });
      return true;
    } catch (e) {
      toast({ title: 'Restablecer contraseña', description: 'Error de red', variant: 'destructive' });
      return false;
    }
  };

  const changePassword = async ({ actual, nueva }) => {
    try {
      const email = user?.email;
      if (!email) {
        toast({ title: 'Cambiar contraseña', description: 'Sesión inválida', variant: 'destructive' });
        return false;
      }
      const resp = await fetch(`${API_URL}/auth/change-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ correo: email, actual, nueva }) });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const message = data.message || 'No fue posible actualizar la contraseña';
        toast({ title: 'cambiar contraseña', description: message, variant: 'destructive' });
        return false;
      }
      toast({ title: 'cambiar contraseña', description: 'Contraseña actualizada correctamente' });
      return true;
    } catch (e) {
      toast({ title: 'cambiar contraseña', description: 'Error de red', variant: 'destructive' });
      return false;
    }
  };

  // `module` puede ser un string o un array de strings. Si es array,
  // se concede el acceso cuando el usuario tiene CUALQUIERA de ellos
  // (lógica "any-of"). Esto permite proteger una ruta que es válida
  // para varios módulos (p.ej. el dashboard de centro, accesible por
  // workplaces O utility_services).
  const hasPermission = (module) => {
    if (!user) return false;
    const modules = Array.isArray(module) ? module : [module];
    const custom = user?.raw?.modulosActivos;

    if (Array.isArray(custom) && custom.length > 0) {
      if (modules.some((m) => custom.includes(m))) return true;
      // Mantener el acceso al módulo de control de empleados para admins
      if (user.role === ROLES.ADMIN && modules.includes(MODULES.CONTROL_EMPLOYEES)) {
        return true;
      }
      return false;
    }

    const allowed = ROLE_PERMISSIONS[user.role] || [];
    return modules.some((m) => allowed.includes(m));
  };

  // Centros de lavado que el usuario puede consultar.
  // Lista vacia = sin restriccion (ve todos los centros activos), misma
  // semantica que modulosActivos. Asi ningun usuario existente cambia.
  const centrosAsignados = Array.isArray(user?.raw?.centrosAsignados)
    ? user.raw.centrosAsignados
    : [];

  // Verificar si el usuario es administrador
  const isAdmin = () => {
    if (!user) return false;
    return user.role === ROLES.ADMIN;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    hasPermission,
    isAdmin,
    refreshUser,
    centrosAsignados,
    ROLES,
    MODULES,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

// Componente de protección de rutas
export const ProtectedRoute = ({ children, requiredModule }) => {
  const { user, loading, hasPermission } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (!loading && requiredModule && !hasPermission(requiredModule)) {
      navigate('/unauthorized');
    }
  }, [user, loading, hasPermission, requiredModule, navigate]);

  if (loading || !user || (requiredModule && !hasPermission(requiredModule))) {
    return <div>Cargando...</div>; // Todo: Implementar un componente de carga
  }

  return children;
};
